/**
 * HUB-391: ink and color-version mutations recompute the run's `ink_mixed` rollup AFTER the write has
 * committed. If that post-commit recompute throws, the server does NOT fail the request (the write
 * already succeeded) — it degrades to attaching a `_warning` string onto the 2xx response body. The
 * write is durable, but the run's `ink_mixed` milestone may now be stale until the next rollup trigger.
 *
 * Bugbot (PR #197) flagged that the client surfaced this `_warning` NOWHERE, so a mixer saw a clean
 * success and trusted a milestone that might be wrong. These helpers extract that message so the calling
 * mutation can raise a non-blocking warning toast telling the user to refresh/verify.
 */

/** Pull a non-empty `_warning` string off an already-parsed response body, or null when absent. */
export function rollupWarningFromBody(body: unknown): string | null {
  const warning = (body as { _warning?: unknown } | null | undefined)?._warning;
  return typeof warning === "string" && warning.trim().length > 0 ? warning : null;
}

/**
 * Read a `_warning` off a raw `Response` (the shape `apiRequest` returns) without consuming its body —
 * `clone()` keeps the original stream readable for any other consumer. Returns null for empty/204 bodies
 * or non-JSON responses, so callers can treat "no warning" and "couldn't parse" identically.
 */
export async function readRollupWarning(response: Response): Promise<string | null> {
  try {
    return rollupWarningFromBody(await response.clone().json());
  } catch {
    return null;
  }
}
